import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Chip,
  Divider,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { T } from '../admin/adminTheme';
import {
  AvailabilityChip,
  BoolChip,
  FilterableSortableTableHead,
  SectionCard,
  StyledTableRow,
  TabHeader,
  TableStateWrapper,
  applyColumnFilters,
  useSortableTable,
} from '../admin/shared';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ;

const COLUMNS = [
  { id: 'MachineCode', label: 'Machine Code', minWidth: 120 },
  { id: 'MachineDescription', label: 'Description', minWidth: 180 },
  { id: 'MachineGroupCode', label: 'Group', minWidth: 100 },
  { id: 'GroupDescription', label: 'Group Description', minWidth: 160 },
  { id: 'HoursType', label: 'Hours Type', minWidth: 110 },
  { id: 'FinishedGood', label: 'FG', minWidth: 70 },
  { id: 'active_status', label: 'Status', minWidth: 120 },
  { id: 'avaliability', label: 'Availability', minWidth: 130 },
  { id: 'activeUserEmpId', label: 'Active User', minWidth: 130 },
];

export default function MasterWorkstationsView({ supervisorId, onSnack }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState({});

  const load = useCallback(async () => {
    if (!supervisorId) return;

    setLoading(true);

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/machinesupervisor/master/workstations/${supervisorId}`
      );

      setList(Array.isArray(res.data) ? res.data : []);
      setColumnFilters({});
    } catch (err) {
      onSnack?.(
        err?.response?.data?.message || 'Failed to load allocated workstations.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [onSnack, supervisorId]);

  useEffect(() => {
    load();
  }, [load]);

  const searchFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;

    return list.filter((machine) => (
      machine.MachineCode?.toLowerCase().includes(q) ||
      machine.MachineDescription?.toLowerCase().includes(q) ||
      machine.MachineGroupCode?.toLowerCase().includes(q) ||
      machine.GroupDescription?.toLowerCase().includes(q) ||
      machine.HoursType?.toLowerCase().includes(q) ||
      String(machine.activeUserEmpId || '').toLowerCase().includes(q)
    ));
  }, [list, search]);

  const columnFilteredRows = useMemo(
    () => applyColumnFilters(searchFiltered, columnFilters),
    [searchFiltered, columnFilters]
  );

  const {
    sortedRows: rows,
    sortField,
    sortDir,
    handleSort,
  } = useSortableTable(columnFilteredRows, 'MachineCode');

  return (
    <Fade in>
      <Box>
        <SectionCard>
          <TabHeader
            count={rows.length}
            noun="Allocated Workstations"
            search={search}
            onSearch={setSearch}
            onRefresh={load}
            hideAdd
          />

          <Divider />

          <TableStateWrapper
            loading={loading}
            empty={rows.length === 0}
            emptyMessage={search ? 'No allocated workstations match your search.' : 'No allocated workstations found.'}
            colSpan={COLUMNS.length}
            minHeight={360}
            skeletonRows={7}
          >
            <TableContainer>
              <Table>
                <FilterableSortableTableHead
                  columns={COLUMNS}
                  sourceRows={searchFiltered}
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                  columnFilters={columnFilters}
                  onColumnFiltersChange={setColumnFilters}
                />

                <TableBody>
                  {rows.map((machine) => (
                    <StyledTableRow key={machine.MachineCode}>
                      <TableCell>
                        <Typography sx={{ fontWeight: 800, fontSize: 13, color: t.blue, fontFamily: 'monospace' }}>
                          {machine.MachineCode}
                        </Typography>
                      </TableCell>

                      <TableCell>{machine.MachineDescription || '—'}</TableCell>

                      <TableCell>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                          {machine.MachineGroupCode || '—'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ color: t.muted }}>
                        {machine.GroupDescription || '—'}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={machine.HoursType === 'Man' ? 'Man Hours' : 'Machine Hours'}
                          color={machine.HoursType === 'Man' ? 'secondary' : 'primary'}
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell>
                        <BoolChip value={machine.FinishedGood} trueLabel="FG" falseLabel="Non-FG" />
                      </TableCell>

                      <TableCell>
                        <BoolChip value={machine.active_status} trueLabel="Enabled" falseLabel="Disabled" />
                      </TableCell>

                      <TableCell>
                        <AvailabilityChip value={machine.avaliability} />
                      </TableCell>

                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontFamily: 'monospace',
                            fontWeight: machine.activeUserEmpId ? 700 : 400,
                            color: machine.activeUserEmpId ? t.primary : t.muted,
                          }}
                        >
                          {machine.activeUserEmpId || '—'}
                        </Typography>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TableStateWrapper>
        </SectionCard>
      </Box>
    </Fade>
  );
}
